import { useMemo } from "react"

import { type ForecastBreakdownRow } from "~/server/api/utils/sharedSqlQueries"
import { api } from "~/utils/api"
import { formatForecast } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"
import { getTailwindValue } from "~/utils/tailwind"
import SuperChart, { type DateChartSeries } from "~/components/reusable/graphs/SuperChart"
import { ChartType, DateType, TodayPlotLine } from "~/constants/graphs"

const ONE_YEAR_MS = 52 * 7 * 24 * 60 * 60 * 1000

type ImputationKey = "stockoutImputation" | "missingSalesImputation"

type Props = {
  data: ForecastBreakdownRow[]
  variantIds: string[]
  valueKey: ImputationKey
  title: string
  seriesName: string
  subtitleLabel: string
  weeks: number
  observationWeeks: number
  total: number
}

export default function ImputationChart({
  data,
  variantIds,
  valueKey,
  title,
  seriesName,
  subtitleLabel,
  weeks,
  observationWeeks,
  total,
}: Props) {
  const { data: weeklySales } = api.variantTableData.weeklySalesHistory.useQuery(
    { variantIds },
    { enabled: variantIds.length > 0 },
  )

  // Imputation values are keyed on the future forecast week but describe last
  // year's data, so shift -52w to align with the actual sales spline.
  const byDate = useMemo(() => {
    const totals = new Map<number, number>()
    for (const row of data) {
      const raw = row[valueKey]
      if (raw == null) continue
      const timestamp = new Date(row.date).getTime() - ONE_YEAR_MS
      totals.set(timestamp, (totals.get(timestamp) ?? 0) + Number(raw))
    }
    return [...totals].sort(([a], [b]) => a - b)
  }, [data, valueKey])

  const lastYearByDate = useMemo(() => {
    if (!weeklySales) return new Map<number, number>()
    const totals = new Map<number, number>()
    for (const rows of weeklySales.values()) {
      for (const row of rows) {
        const ms = new Date(row.date).getTime()
        totals.set(ms, (totals.get(ms) ?? 0) + Number(row.sales))
      }
    }
    return totals
  }, [weeklySales])

  const series = useMemo((): DateChartSeries[] => {
    const imputed: DateChartSeries = {
      name: seriesName,
      // Null out sub-1 weeks so the minPointLength floor only lifts whole-unit bars,
      // not the empty weeks (minPointLength would otherwise show 0s as slivers).
      data: byDate.map(([ms, value]) => ({ milliseconds: ms, value: value >= 1 ? value : null })),
      type: "column",
      colors: [getTailwindValue("--color-orange-500") || "#f97316"],
    }
    const sales: DateChartSeries = {
      name: `Last year ${formatName("sale", true)}`,
      data: [...lastYearByDate]
        .sort(([a], [b]) => a - b)
        .map(([ms, value]) => ({ milliseconds: ms, value })),
      type: "spline",
      colors: [getTailwindValue("--color-blue-500") || "#3b82f6"],
      dashStyle: "Dash",
    }
    return [imputed, sales]
  }, [byDate, lastYearByDate, seriesName])

  if (byDate.length === 0) return null

  const salesMs = [...lastYearByDate.keys()]
  const allMs = [...byDate.map(([ms]) => ms), ...salesMs]
  const dateRange = {
    start: new Date(Math.min(...allMs)),
    end: new Date(Math.max(...allMs)),
  }

  return (
    <div>
      <div className="label-base text-fg-base mb-0.5">{title}</div>
      <div className="label-sm text-fg-subtle mb-1">
        {subtitleLabel}{" "}
        <span className="text-fg-base">
          {weeks} of {observationWeeks} weeks
        </span>
        {" · "}Imputed: <span className="text-fg-base">{formatForecast(total)}</span>
      </div>
      <SuperChart
        type={ChartType.TimeSeries}
        data={series}
        dateRange={dateRange}
        dateType={DateType.Weekly}
        height={340}
        disableAnimation
        todayPlotLine={TodayPlotLine.Hide}
        minColumnPointLength={3}
        yAxis={[{ title: { text: undefined }, min: 0 }]}
      />
    </div>
  )
}
